import type { ReactNode } from 'react';
import { findAppByProductKey, type ProductKey } from '../../lib/app-registry';
import { trpc } from '../../lib/trpc';
import { SeatPausedNotice } from './SeatPausedNotice';

interface SeatStatusGateProps {
  /** The product this portal gates seats for. Omit to render `children` unconditionally. */
  productKey?: ProductKey;
  children: ReactNode;
}

/**
 * Wraps a portal's route tree and blocks it with `SeatPausedNotice` when the signed-in user's own
 * seat is paused (assigned, but over the plan's current cap) — the one choke point every route
 * already shares, so this replaces what would otherwise be every individual content query 403ing
 * independently with no unified explanation. `workspaceId` must already be in the URL by the time
 * this mounts; it's meant to sit inside a workspace-scoped route, not above one.
 */
export function SeatStatusGate({ productKey, children }: SeatStatusGateProps) {
  const { data: billingState, isLoading } = trpc.workspace.getBillingState.useQuery(
    { productKey: productKey as ProductKey },
    { enabled: !!productKey }
  );

  if (!productKey) return <>{children}</>;
  if (isLoading) return null; // Brief empty frame, matching the workspace-switch loading gap.

  if (billingState?.viewerSeatStatus === 'paused') {
    return (
      <SeatPausedNotice
        productLabel={findAppByProductKey(productKey)?.displayName ?? productKey}
        isPastDue={billingState.stripeStatus === 'past_due'}
      />
    );
  }

  return <>{children}</>;
}
